import { Star, ArrowRight, Heart, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServiceCategory } from '@/lib/types';
import { serviceImages } from '@/lib/service-images';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface Props {
  service: ServiceCategory;
  index: number;
  isFavorite?: boolean;
  onToggleFavorite?: (serviceId: string) => void;
  searchMatch?: string | null;
}

import { useQueryClient } from '@tanstack/react-query';
import { fetchServiceDetail } from '@/hooks/useServices';
import { useEffect, useRef, useState, memo } from 'react';

const ServiceCard = memo(({ service, index, isFavorite = false, onToggleFavorite, searchMatch }: Props) => {
  const [imageError, setImageError] = useState(false);
  const image = (!imageError && (service.imageUrl || serviceImages[service.id] || (service.slug && serviceImages[service.slug]))) || null;
  const queryClient = useQueryClient();
  const cardRef = useRef<HTMLDivElement>(null);

  // Million-User Performance: Smart Prefetching 🚀
  // Loads details in background when card is hovered or visible
  const prefetchDetails = () => {
    queryClient.prefetchQuery({
      queryKey: ['service-detail', service.slug || service.id],
      queryFn: () => fetchServiceDetail(service.slug || service.id),
      staleTime: 15 * 60 * 1000,
    });
  };

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        prefetchDetails();
        observer.disconnect(); // Only prefetch once per mount
      }
    }, { threshold: 0.5 });

    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [service.slug, service.id]);

  const handleFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleFavorite?.(service.id);
  };

  const cheapestSub = (service.subServices || []).length > 0 
    ? service.subServices!.reduce((prev, curr) => prev.price < curr.price ? prev : curr, service.subServices![0])
    : null;

  // 🔒 SACRED: Correct reverse-discount formula — selling price is NEVER modified
  // MRP = Price / (1 - Discount/100)
  const mrp = cheapestSub 
    ? (cheapestSub.mrpOverride 
      || (cheapestSub.mrpMarkupPercentage && cheapestSub.mrpMarkupPercentage > 0 && cheapestSub.mrpMarkupPercentage < 100
        ? Math.round(cheapestSub.price / (1 - cheapestSub.mrpMarkupPercentage / 100))
        : null))
    : null;

  const discountPercent = (mrp && cheapestSub && mrp > cheapestSub.price) ? Math.round(((mrp - cheapestSub.price) / mrp) * 100) : null;

  const isRange = service.priceType === 'range' && service.priceMin > 0 && service.priceMax > 0;
  
  const priceDisplay = isRange
    ? `₹${service.priceMin} - ₹${service.priceMax}`
    : `₹${service.startingPrice}+`;

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: index * 0.05, duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      <Link
        to={`/service/${service.slug}`}
        onMouseEnter={prefetchDetails}
        className="group relative block rounded-2xl bg-card overflow-hidden shadow-card hover:shadow-elevated transition-all duration-500 border border-border/50"
      >
        {/* Image Area */}
        <div className="relative h-28 sm:h-32 md:h-36 lg:h-40 overflow-hidden flex items-center justify-center" style={{ background: 'linear-gradient(135deg, hsl(0 0% 92%), hsl(0 0% 86%), hsl(0 0% 90%))' }}>
          <div className="absolute -right-4 -top-4 h-20 w-20 rounded-full bg-primary/[0.08]" />
          <div className="absolute -left-3 -bottom-3 h-14 w-14 rounded-full bg-primary/[0.06]" />

          {image ? (
            <motion.img
              src={image}
              alt={service.name}
              onError={() => setImageError(true)}
              className="h-24 w-24 sm:h-26 sm:w-26 md:h-28 md:w-28 lg:h-32 lg:w-32 object-contain drop-shadow-[0_12px_24px_rgba(0,0,0,0.2)] relative z-10 filter saturate-[1.15] brightness-[1.05]"
              whileHover={{ scale: 1.15, y: -6 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            />
          ) : (
            <span className="text-5xl md:text-6xl">{service.icon}</span>
          )}

          <div className="absolute top-2 right-2 flex items-center gap-0.5 bg-card/80 backdrop-blur-sm rounded-full px-2 py-0.5 text-[10px] md:text-[11px] font-semibold shadow-sm">
            <Star className="h-2.5 w-2.5 fill-warning text-warning" />
            {service.rating}
          </div>

          {/* Favorite button */}
          {onToggleFavorite && (
            <button
              onClick={handleFavorite}
              className="absolute top-2 left-2 p-1.5 rounded-full bg-card/80 backdrop-blur-sm shadow-sm hover:bg-card transition-colors z-20"
            >
              <Heart className={cn('h-3.5 w-3.5 transition-colors', isFavorite ? 'fill-destructive text-destructive' : 'text-muted-foreground')} />
            </button>
          )}

          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary-foreground/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
        </div>

        {/* Content */}
        <div className="p-3 sm:p-3.5 md:p-4">
          <div className="flex items-center justify-between mb-0.5">
            <h3 className="font-bold text-xs sm:text-sm md:text-base group-hover:text-primary transition-colors duration-300 truncate">{service.name}</h3>
            <motion.div
              className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 ml-1 flex-shrink-0"
              whileHover={{ x: 2 }}
            >
              <ArrowRight className="h-3.5 w-3.5 text-primary" />
            </motion.div>
          </div>
          
          {searchMatch && (
            <div className="flex items-center gap-1 mb-2 px-1.5 py-0.5 bg-primary/10 rounded-md w-fit">
              <Sparkles className="h-2.5 w-2.5 text-primary" />
              <span className="text-[10px] font-bold text-primary truncate">Matches: {searchMatch}</span>
            </div>
          )}

          <p className="text-[10px] sm:text-[11px] md:text-xs text-muted-foreground mb-2 line-clamp-1">{service.description}</p>
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <div className="flex items-baseline gap-1.5">
                <span className="text-sm sm:text-base font-bold text-primary">{priceDisplay}</span>
                {mrp && !isRange && (
                  <span className="text-[10px] sm:text-[11px] text-muted-foreground line-through decoration-muted-foreground/50">
                    ₹{mrp}+
                  </span>
                )}
              </div>
              {mrp && !isRange && discountPercent && (
                <div className="mt-0.5">
                  <span className="text-[9px] sm:text-[10px] font-bold text-success bg-success/10 px-1.5 py-0.5 rounded-sm border border-success/10">
                    SAVE {discountPercent}%
                  </span>
                </div>
              )}
            </div>
            <span className="text-[10px] sm:text-[11px] text-muted-foreground hidden sm:inline">
              {service.reviewCount.toLocaleString()} reviews
            </span>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary/0 via-primary to-primary/0 scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
      </Link>
    </motion.div>
  );
});

ServiceCard.displayName = 'ServiceCard';

export default ServiceCard;
